import { useState } from "react";
import { FileText, Download, Trash2, ScanText, Loader2, CheckCircle2, Eye, EyeOff, Pencil, Check, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { labelDocument } from "@/lib/dataroom";

const EXTRACTIBLE = /\.(pdf|csv|txt|xlsx|xlsm|xls)$/i;

export default function DocumentRow({ doc, onDownload, onDelete, onExtract, onToggleAnalyse, onRemarques, onRename, selected, onToggleSelect, extracting, datePending }) {
    const date = new Date(doc.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    const extracted = !!(doc.contenu_extrait && doc.contenu_extrait.trim());
    const included = doc.inclure_analyse !== false;
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(doc.nom_fichier || "");

    const commitRename = () => {
        const v = name.trim();
        setEditing(false);
        if (v && v !== doc.nom_fichier) onRename?.(doc, v);
        else setName(doc.nom_fichier || "");
    };

    return (
        <li data-testid={`document-row-${doc.id}`} className={`px-5 py-3 flex items-center gap-4 ${included ? "" : "opacity-60"} ${selected ? "bg-indigo-50/60" : ""}`}>
            {onToggleSelect && (
                <input
                    type="checkbox"
                    data-testid={`document-select-${doc.id}`}
                    checked={!!selected}
                    onChange={() => onToggleSelect(doc.id)}
                    title="Sélectionner"
                    className="h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
            )}
            <button
                type="button"
                data-testid={`document-toggle-analyse-${doc.id}`}
                onClick={() => onToggleAnalyse(doc)}
                title={included ? "Inclus dans l'analyse IA — cliquer pour exclure" : "Exclu de l'analyse IA — cliquer pour inclure"}
                aria-pressed={included}
                className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors ${included ? "text-primary hover:bg-primary/10" : "text-muted-foreground hover:bg-accent"}`}
            >
                {included ? <Eye className="h-4 w-4" strokeWidth={1.75} /> : <EyeOff className="h-4 w-4" strokeWidth={1.75} />}
            </button>
            <FileText className="h-5 w-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
            <div className="min-w-0 flex-1">
                {editing ? (
                    <div className="flex items-center gap-1.5">
                        <input
                            data-testid={`document-rename-input-${doc.id}`}
                            value={name}
                            autoFocus
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") { setEditing(false); setName(doc.nom_fichier || ""); } }}
                            className="h-7 w-full max-w-sm rounded-md border border-indigo-400 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                        />
                        <button type="button" data-testid={`document-rename-save-${doc.id}`} onClick={commitRename} title="Valider" className="h-7 w-7 rounded-md flex items-center justify-center text-emerald-600 hover:bg-emerald-50"><Check className="h-4 w-4" /></button>
                        <button type="button" data-testid={`document-rename-cancel-${doc.id}`} onClick={() => { setEditing(false); setName(doc.nom_fichier || ""); }} title="Annuler" className="h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
                    </div>
                ) : (
                    <div className="flex items-center gap-1.5">
                        <p data-testid="document-name" className="text-sm font-medium text-foreground truncate">{doc.nom_fichier}</p>
                        {onRename && (
                            <button type="button" data-testid={`document-rename-${doc.id}`} onClick={() => { setName(doc.nom_fichier || ""); setEditing(true); }} title="Renommer" className="shrink-0 text-slate-400 hover:text-indigo-600"><Pencil className="h-3.5 w-3.5" /></button>
                        )}
                    </div>
                )}
                <p className="text-xs text-muted-foreground">
                    Déposé le {date}
                    {doc.uploaded_by_email ? <> · <span data-testid="document-uploader" className="text-primary font-medium">{doc.uploaded_by_email}</span> (portail client)</> : " · cabinet"}
                </p>
                {onRemarques && (
                    <>
                        {datePending && (
                            <p data-testid={`document-date-pending-${doc.id}`} className="mt-1 flex items-center gap-1.5 text-xs text-indigo-600">
                                <Loader2 className="h-3 w-3 animate-spin" /> Lecture IA de la date en cours…
                            </p>
                        )}
                        <input
                            key={`rem-${doc.id}-${doc.remarques || ""}`}
                            data-testid={`document-remarques-${doc.id}`}
                            defaultValue={doc.remarques || ""}
                            placeholder="Remarques (période, réserve, contexte…)"
                            onBlur={(e) => e.target.value.trim() !== (doc.remarques || "") && onRemarques(doc, e.target.value)}
                            className="mt-1 h-7 w-full max-w-md rounded-md border border-slate-300 bg-white px-2 text-xs placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        />
                    </>
                )}
            </div>
            {doc.periode && (
                <Badge data-testid="document-periode" variant="outline" className="rounded-full border-border text-muted-foreground shadow-sm font-mono text-xs">
                    {doc.periode}
                </Badge>
            )}
            <Badge data-testid="document-type" className="rounded-full bg-primary/10 text-primary border-transparent shadow-sm font-medium">
                {doc.type_document === "autre" && doc.type_personnalise ? `Autre · ${doc.type_personnalise}` : labelDocument(doc.type_document)}
            </Badge>
            {extracted ? (
                <span data-testid={`document-extracted-${doc.id}`} title="Texte extrait" className="flex items-center gap-1 text-xs text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Extrait
                </span>
            ) : EXTRACTIBLE.test(doc.nom_fichier || "") && (
                <button
                    type="button"
                    data-testid={`document-extract-${doc.id}`}
                    onClick={() => onExtract(doc)}
                    disabled={extracting}
                    className="h-8 px-3 rounded-full flex items-center gap-1.5 text-xs font-medium border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-60 transition-colors"
                >
                    {extracting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanText className="h-3.5 w-3.5" strokeWidth={1.75} />}
                    {extracting ? "Extraction…" : "Extraire le texte"}
                </button>
            )}
            <button
                type="button"
                data-testid={`document-download-${doc.id}`}
                onClick={() => onDownload(doc)}
                title="Télécharger"
                className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
            >
                <Download className="h-4 w-4" strokeWidth={1.75} />
            </button>
            <button
                type="button"
                data-testid={`document-delete-${doc.id}`}
                onClick={() => window.confirm(`Supprimer « ${doc.nom_fichier} » ?`) && onDelete(doc)}
                title="Supprimer"
                className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            >
                <Trash2 className="h-4 w-4" strokeWidth={1.75} />
            </button>
        </li>
    );
}
